import type { FC } from 'react'
import { useEffect } from 'react'

import { InfoBox, Skeleton } from '@skytech/ui'
import { useQuery } from '@tanstack/react-query'
import orderBy from 'lodash/orderBy'

import { Translation } from '@/app/translations/Translation'
import { NoResults } from '@/components/NoResults'
import { useRenderLimit } from '@/hooks/use-render-limit'
import queries from '@/modules/ecom-policy/queries'
import ecomPolicyResources from '@/modules/ecom-policy/resources'
import { useShopNewOrderContext } from '@/modules/ecom-policy/shop/new-order/ShopNewOrder.context'
import type { CategoryData, ProductGroupData } from '@/modules/ecom-policy/types'

import Filter from '../Filter'
import ProductGroup from '../ProductGroup'
import ProgressButtons from '../ProgressButtons'

import '../products.scss'

const StepProducts: FC<{
  orderSummary:
    | {
        cost: number
        products: number
        currency: string | null
      }
    | undefined
  availableCategories: CategoryData[]
  groupedAndFiltered: Record<string, ProductGroupData>
  isAccessoriesAvailable: boolean
  isLoading: boolean
  advanceWizard?: () => void
  regressWizard?: () => void
}> = ({
  orderSummary,
  availableCategories,
  groupedAndFiltered,
  isAccessoriesAvailable,
  isLoading,
  advanceWizard,
  regressWizard,
}) => {
  const shopCtx = useShopNewOrderContext()
  const [, setSearchQuery] = shopCtx.searchQuery
  const [, setSelectedCategoryIds] = shopCtx.selectedCategoryIds
  const [selectedPolicyId] = shopCtx.selectedPolicyId
  const [, setStockFilter] = shopCtx.stockFilter
  const [sortParam, setSortParam] = shopCtx.sortParam
  const [sortOrder, setSortOrder] = shopCtx.sortOrder
  const [, setActiveStepName] = shopCtx.activeStepName

  const { data: policy, isLoading: isPolicyDetailsLoading } = useQuery(
    selectedPolicyId ? queries.policyDetails(selectedPolicyId) : [''],
    () => {
      if (!selectedPolicyId) throw Error('No policy selected!')
      return ecomPolicyResources.getPolicyDetails(selectedPolicyId)
    }
  )

  useEffect(() => {
    setActiveStepName('PRODUCTS')
  }, [setActiveStepName])

  useEffect(
    // Reset filters when leaving this step
    () => () => {
      setStockFilter(false)
      setSortParam(undefined)
      setSortOrder('asc')
      setSelectedCategoryIds(null)
      setSearchQuery('')
    },
    [setSearchQuery, setSelectedCategoryIds, setSortOrder, setSortParam, setStockFilter]
  )

  const sortedInventoryList = orderBy(groupedAndFiltered, sortParam, sortOrder)
  const { visibleList, ListLoaderComponent } = useRenderLimit(
    Object.values(sortedInventoryList) || [],
    24
  )

  return (
    <div>
      <Filter availableCategories={availableCategories || []} />

      {isLoading || isPolicyDetailsLoading ? (
        <ProductsSkeleton />
      ) : visibleList.length === 0 ? (
        <NoResults>
          <Translation id="No products..." />
        </NoResults>
      ) : (
        <>
          {policy?.stepMainDescription && (
            <InfoBox emphasis="warning" marginBottom={16} marginTop={16}>
              <h3 className="m-0 whitespace-pre-wrap text-sm font-normal">
                {policy.stepMainDescription}
              </h3>
            </InfoBox>
          )}

          <div className="inventory__products">
            {visibleList.map(group => (
              <ProductGroup key={group.groupKey} {...group} />
            ))}
          </div>
        </>
      )}

      <ListLoaderComponent />

      <ProgressButtons
        orderSummary={orderSummary}
        isAccessoriesAvailable={isAccessoriesAvailable}
        advanceWizard={advanceWizard}
        regressWizard={regressWizard}
      />
    </div>
  )
}

export default StepProducts

const ProductsSkeleton: FC = () => {
  const tenElements = new Array(10)
  tenElements.fill(null)

  return (
    <div className="grid grid-cols-[repeat(auto-fill,minmax(13rem,1fr))]">
      {tenElements.map((_, index) => (
        <div
          key={index}
          className="pointer-events-none m-1 rounded-md border border-transparent bg-transparent p-0"
        >
          <Skeleton height="216px" />
        </div>
      ))}
    </div>
  )
}
